import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import DashboardWrapper from "../../components/DashboardWrapper";
import CrudTable from "../../components/dashboard/table/CrudTable";
import CrudForm from "../../components/dashboard/form/CrudForm";
import { useCrud } from "../../hooks/useCrud";
import Swal from "sweetalert2";
import { validateName, validateSeoDescription, validateSeoKeywords, validateSeoTitle } from "../../utils/Validations";

const generateSlug = (text) =>
  text.toString().toLowerCase().trim().replace(/\s+/g, "-");

export default function SubCategory() {
  const navigate = useNavigate();

  const {
    data: subCategories = [],
    loading: subLoading,
    errors: subErrors,
    createItem,
    updateItem,
    deleteItem,
  } = useCrud(`${process.env.REACT_APP_API_URL}/backend/sub-categories`);

  const {
    data: categories = [],
    loading: catLoading,
    errors: catErrors,
  } = useCrud(`${process.env.REACT_APP_API_URL}/backend/categories`);

  const [editingItem, setEditingItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const allowedFields = useMemo(() => ["category_id", "name", "status", "seo_title", "seo_description", "seo_keywords", "description"], []);

  //   useEffect(() => {
  //     if (subErrors.auth || subErrors.fetch?.includes("Unauthorized") || catErrors.auth || catErrors.fetch?.includes("Unauthorized")) {
  //       Swal.fire("Session Expired", "Please log in again.", "error");
  //       navigate("/login");
  //     }
  //   }, [subErrors, catErrors, navigate]);

  const getFormData = useCallback(
    (item) => {
      if (!item) {
        return {
          category_id: "",
          name: "",
          status: 1,
          seo_title: "",
          seo_description: "",
          seo_keywords: "",
          description: "",
        };
      }
      return allowedFields.reduce((acc, field) => {
        acc[field] =
          field === "status"
            ? Number(item[field] ?? 1)
            : item[field] || "";
        return acc;
      }, {});
    },
    [allowedFields]
  );

  const hasDuplicate = useCallback(
    (name, slug, id = null) =>
      subCategories.some(
        (item) =>
          (item.name.toLowerCase() === name.toLowerCase() ||
            item.slug.toLowerCase() === slug.toLowerCase()) &&
          item.id !== id
      ),
    [subCategories]
  );

 const showSwal = useCallback((title, text, icon) => {
  Swal.fire({
    title,
    text,
    icon,
    confirmButtonText: "OK",
    customClass: {
      confirmButton: "btn btn-primary",
    },
    buttonsStyling: false,

    didOpen: () => {
      const container = document.querySelector(".swal2-container");

      if (container) {
        container.style.zIndex = "20000";
      }
    },
  });
}, []);

  const handleSubmit = useCallback(
    async (formData) => {
     const nameError = validateName(formData.name);

if (nameError !== true) {
  showSwal("Validation Error!", nameError, "warning");
  return;
}

const seoTitleError = validateSeoTitle(formData.seo_title);

if (seoTitleError !== true) {
  showSwal("Validation Error!", seoTitleError, "warning");
  return;
}

const seoDescriptionError = validateSeoDescription(
  formData.seo_description
);

if (seoDescriptionError !== true) {
  showSwal("Validation Error!", seoDescriptionError, "warning");
  return;
}

const seoKeywordsError = validateSeoKeywords(
  formData.seo_keywords
);

if (seoKeywordsError !== true) {
  showSwal("Validation Error!", seoKeywordsError, "warning");
  return;
}

if (!formData.category_id) {
  showSwal("Error!", "Category must be selected.", "warning");
  return;
}

formData.name = formData.name.trim();
formData.seo_title = formData.seo_title.trim();
formData.seo_description = formData.seo_description.trim();
formData.seo_keywords = formData.seo_keywords.trim();

      formData.slug = generateSlug(formData.name);

      if (hasDuplicate(formData.name, formData.slug, editingItem?.id)) {
        showSwal(
          "Duplicate Found!",
          "A sub category with the same name or slug already exists.",
          "warning"
        );
        return;
      }

      const dataToSend = {};
      allowedFields.forEach((field) => {
        dataToSend[field] = formData[field];
      });
      dataToSend.slug = formData.slug;

      try {
        if (editingItem) {
          await updateItem(editingItem.id, dataToSend);
          showSwal("Updated!", "The sub category has been updated.", "success");
        } else {
          await createItem(dataToSend);
          showSwal("Created!", "A new sub category has been created.", "success");
        }
        setModalOpen(false);
        setEditingItem(null);
      } catch (error) {
        showSwal("Error!", error.message || "Something went wrong.", "error");
      }
    },
    [allowedFields, editingItem, updateItem, createItem, hasDuplicate, showSwal]
  );

  const openModal = useCallback((item = null) => {
    if (!categories.length && !catLoading) {
      showSwal("Error!", "No categories available. Please create a category first.", "warning");
      return;
    }
    setEditingItem(item);
    setModalOpen(true);
  }, [categories, catLoading, showSwal]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingItem(null);
  }, []);

  const handleDelete = useCallback(
    (id) => {
      Swal.fire({
        title: "Are you sure?",
        text: "This action cannot be undone!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "Cancel",
        customClass: {
          confirmButton: "btn btn-primary m-1",
          cancelButton: "btn btn-danger m-1",
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await deleteItem(id);
            showSwal("Deleted!", "The sub category has been deleted.", "success");
          } catch (error) {
            showSwal("Error!", error.message || "Delete failed.", "error");
          }
        }
      });
    },
    [deleteItem, showSwal]
  );

  const handleToggleStatus = useCallback(
    async (item) => {
      try {
        const updatedData = {
          category_id: item.category_id,
          name: item.name,
          slug: item.slug,
          status: item.status ? 0 : 1,
        };
        await updateItem(item.id, updatedData);
        showSwal("Success!", "Sub-category status updated.", "success");
      } catch {
        showSwal("Error!", "Failed to update status.", "error");
      }
    },
    [updateItem, showSwal]
  );

  const tableData = useMemo(
    () =>
      subCategories.map((item, idx) => {
        const cat = categories.find((c) => c.id === item.category_id);
        return {
          ...item,
          sl: idx + 1,
          category_name: cat ? cat.name : "—",
        };
      }),
    [subCategories, categories]
  );

  return (
    <DashboardWrapper>

      <div className="container-fluid mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <button
            className="btn btn-secondary d-flex align-items-center"
            onClick={() => navigate(-1)}
          >

            <span>Back</span>
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => openModal()}
          >
            Create Sub Category
          </button>
        </div>

        <div className="card shadow">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="mb-0">Sub Categories</h4>
          </div>



          {modalOpen && (
            <div
              className="modal fade show"
              tabIndex={-1}
              style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
              aria-modal="true"
              role="dialog"
            >
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header d-flex justify-content-between align-items-center">
                    <h5 className="modal-title">
                      {editingItem ? "Edit Sub Category" : "Create Sub Category"}
                    </h5>
                    <button
                      type="button"
                      className="btn-close .btn-danger"
                      aria-label="Close"
                      onClick={closeModal}
                    />
                  </div>
                  <div className="modal-body">
                    {catLoading ? (
                      <div className="text-center p-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : (
                      <CrudForm
                        initialData={getFormData(editingItem)}
                        onSubmit={handleSubmit}
                        allowedFields={allowedFields}
                        categories={categories}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {subLoading && !tableData.length ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: 200 }}
            >
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="card-body">
              <CrudTable
                data={tableData}
                onEdit={openModal}
                onDelete={handleDelete}
                onToggleStatus={handleToggleStatus}
                columns={["sl", "id", "name", "category_name", "status"]}
              />
            </div>
          )}
        </div>
      </div>
    </DashboardWrapper>
  );
}